import React from 'react'
import { Mail, PhoneOutgoing } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'

import { Footer } from '@frame/ui-components/footer'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { A } from '@frame/ui-primitives/a'
import { Body } from '@frame/ui-primitives/body'
import { Button } from '@frame/ui-primitives/button'
import { H2 } from '@frame/ui-primitives/h2'
import { H4 } from '@frame/ui-primitives/h4'
import { P } from '@frame/ui-primitives/p'
import { Span } from '@frame/ui-primitives/span'

import SecurityImage from '@frame/images/security.png'
import { TrueworkJsFeedbackReason as FeedbackReason } from '@frame/lib/api/common/types'
import { InternalEventType } from '@frame/lib/api/events/types'
import { TW_EMAIL, TW_PHONE } from '@frame/lib/constants'
import { useRootContext } from '@frame/lib/context'
import { useAutoNavigate } from '@frame/lib/hooks/useAutoNavigate'
import { useEventHandler } from '@frame/lib/hooks/useEventHandler'
import { useStore } from '@frame/lib/stores/useStore'
import { cn } from '@frame/lib/utils/cn'
import { isSessionInTerminalState } from '@frame/lib/utils/session'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'
import { Label, Radio, RadioGroup } from '@headlessui/react'

const options = [
  {
    label: "I don't know my payroll login",
    value: FeedbackReason.DONT_KNOW_PAYROLL_LOGIN,
    Tip: () => (
      <div className="flex flex-col text-moss-700">
        <Span className="text-xs font-bold">Pro Tip</Span>
        <P>Try resetting your password by going to your payroll provider's login page.</P>
      </div>
    ),
  },
  {
    label: "Can't find employer or payroll system",
    value: FeedbackReason.UNABLE_TO_FIND_EMPLOYER,
    Tip: () => (
      <div className="flex flex-col text-moss-700">
        <Span className="text-xs font-bold">Pro Tip</Span>
        <P>
          Ask your employer which payroll system you use. If you still can't log in, we will try verifying you through
          other sources.
        </P>
        <div className="flex flex-col space-y-3">
          <Span className="mt-3 text-sm font-bold">Still need help?</Span>
          <div className="flex items-center text-electric-650">
            <Mail className="mr-3 h-4 w-4" />
            <A className="text-sm font-medium" href={`mailto:${TW_EMAIL}`} target="_blank" rel="noreferrer">
              {TW_EMAIL}
            </A>
          </div>
          <div className="flex items-center text-electric-650">
            <PhoneOutgoing className="mr-3 h-4 w-4" />
            <A className="text-sm font-medium" href={`tel:${TW_PHONE}`} target="_blank" rel="noreferrer">
              {TW_PHONE}
            </A>
          </div>
        </div>
      </div>
    ),
  },
  {
    label: "I didn't feel comfortable entering my username or password",
    value: FeedbackReason.UNCOMFORTABLE_ENTERING_CREDENTIALS,
    Tip: ({ onChange: onClick }: { onChange: (value?: string) => void }) => (
      <div>
        <div className="flex text-moss-700">
          <img className="mr-4 h-[56px] w-[56px] object-contain object-center" src={SecurityImage} />
          <Span className="text-sm">
            Security and integrity of your data is our #1 priority. Your data is only used to complete your
            application.&nbsp;
            <A
              href="https://help.truework.com/hc/en-us/articles/16766689282839-Truework-Credentials-A-Comprehensive-Security-Overview"
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            </A>
          </Span>
        </div>
        <div className="-ml-[28px] mt-4 w-[calc(100%+28px)] ">
          <Button intent="secondary" onClick={() => onClick()}>
            Back to verification
          </Button>
        </div>
      </div>
    ),
  },
  {
    label: "I don't have an employer",
    value: FeedbackReason.DONT_HAVE_EMPLOYER,
    Tip: () => (
      <div className="flex flex-col text-moss-700">
        <Span className="text-xs font-bold">Pro Tip</Span>
        <P>No worries, we will try verifying you through other sources.</P>
      </div>
    ),
  },
  {
    label: 'Something else?',
    value: FeedbackReason.OTHER,
    Tip: ({
      value,
      onChange,
      error,
      disabled,
    }: {
      value?: string
      onChange?: (value: string) => void
      error?: boolean
      disabled?: boolean
    }) => (
      <div className="-ml-[28px] flex w-[calc(100%+28px)] flex-col text-moss-950">
        <input
          className="z-50 rounded-[4px] border border-solid border-outline px-4 py-[14px] text-sm placeholder:text-placeholder"
          value={value}
          onChange={(e) => {
            onChange?.(e.currentTarget.value)
          }}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder="Please provide your reason"
          disabled={disabled}
          data-cy={DataAttribute.FEEDBACK_OTHER_INPUT}
        />
        {error ? <Span className="mt-1 text-xs font-semibold text-error">Required</Span> : null}
        <div className="flex flex-col space-y-3">
          <Span className="mt-3 text-sm font-bold">Still need help?</Span>
          <div className="flex items-center text-electric-650">
            <Mail className="mr-3 h-4 w-4" />
            <A className="text-sm font-medium" href={`mailto:${TW_EMAIL}`} target="_blank" rel="noreferrer">
              {TW_EMAIL}
            </A>
          </div>
          <div className="flex items-center text-electric-650">
            <PhoneOutgoing className="mr-3 h-4 w-4" />
            <A className="text-sm font-medium" href={`tel:${TW_PHONE}`} target="_blank" rel="noreferrer">
              {TW_PHONE}
            </A>
          </div>
        </div>
      </div>
    ),
  },
]

export function Feedback() {
  const navigate = useNavigate()
  const { autoNavigate } = useAutoNavigate()
  const { actions } = useRootContext()
  const { handleEvent } = useEventHandler()
  const { sessionState, instanceId } = useStore((state) => ({
    sessionState: state.session?.state,
    instanceId: state.instanceId,
  }))
  const [selected, setSelected] = React.useState<FeedbackReason | null>(null)
  const [otherReason, setOtherReason] = React.useState<string>()
  const [done, setDone] = React.useState(false)
  const [otherReasonError, setOtherReasonError] = React.useState(false)
  const { trigger, isMutating } = useSWRMutation(
    ['feedback'],
    (_key, { arg }: { arg: { reason: FeedbackReason } }) => {
      return actions.addFeedback({ reason: arg.reason, instanceId: instanceId, otherReason: otherReason })
    },
    {
      onSuccess: ({ response }) => {
        if (response.ok) {
          setDone(true)
          handleEvent(InternalEventType.FEEDBACK_SUBMITTED, {
            partner_event: {
              feedback: selected,
              other_feedback: otherReason,
            },
          })
        }
      },
    }
  )

  const shouldHideBackBtn = done || isSessionInTerminalState(sessionState)

  async function handleSubmit() {
    if (selected === FeedbackReason.OTHER && !otherReason?.length) {
      setOtherReasonError(true)
      return
    }
    if (selected) {
      await trigger({ reason: selected })
      navigate(routes.feedbackComplete)
    }
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        showCobranding
        cancelButton={{ visible: false }}
        backButton={{
          visible: !shouldHideBackBtn,
          onClick: () => {
            navigate(-1)
            handleEvent(InternalEventType.RETURNED_FROM_FEEDBACK)
          },
        }}
      />
      <Body data-cy={DataAttribute.FEEDBACK_SCREEN} className="p-5" paddingTopOverride={0}>
        <div className="mb-2 flex items-center">
          <H2>Verification incomplete!</H2>
        </div>
        <P>Don&apos;t forget to come back and complete your verification.</P>
        <div className="my-5">
          <Span className="font-semibold">Running into trouble?</Span>
        </div>
        <RadioGroup value={selected} onChange={setSelected}>
          <Label className="sr-only">Feedback options</Label>
          <div className="flex flex-col space-y-3">
            {options.map((option) => (
              <Radio key={option.value} value={option.value} disabled={done}>
                {({ checked, disabled }) => (
                  <div
                    className={cn(
                      'flex cursor-pointer flex-col rounded-[4px] border border-solid border-transparent bg-moss-100 p-4 transition-all',
                      checked && 'border-brand bg-purple-light',
                      disabled && 'cursor-not-allowed'
                    )}
                    data-cy={
                      option.value === FeedbackReason.OTHER
                        ? DataAttribute.FEEDBACK_OTHER_OPTION
                        : DataAttribute.FEEDBACK_OPTION
                    }
                  >
                    <div className="flex items-center">
                      <div
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-solid border-[##8D91A5] bg-white transition-all',
                          checked && 'border-none bg-electric-650'
                        )}
                      >
                        {checked ? (
                          <div className="ml-[0.5px] mt-[0.5px] h-2 w-2 shrink-0 rounded-full bg-white" />
                        ) : null}
                      </div>
                      <Label
                        as={H4}
                        className={cn('ml-3 !text-moss-700 transition-all', checked && '!text-electric-650')}
                      >
                        {option.label}
                      </Label>
                    </div>
                    {checked ? (
                      <RadioGroup.Description as="div" className="ml-[28px] mt-4">
                        <option.Tip
                          value={otherReason}
                          onChange={(value?: string) => {
                            if (selected === FeedbackReason.UNCOMFORTABLE_ENTERING_CREDENTIALS) {
                              autoNavigate()
                              return
                            }
                            if (!value?.length) {
                              setOtherReasonError(true)
                            } else {
                              setOtherReasonError(false)
                            }
                            setOtherReason(value?.length ? value : undefined)
                          }}
                          error={otherReasonError}
                          disabled={done}
                        />
                      </RadioGroup.Description>
                    ) : null}
                  </div>
                )}
              </Radio>
            ))}
          </div>
        </RadioGroup>
      </Body>
      <Footer
        buttons={{
          primary: (
            <Button
              data-cy={DataAttribute.FEEDBACK_SUBMIT_BUTTON}
              applyCobranding
              disabled={!selected || isMutating || done}
              onClick={handleSubmit}
            >
              Share my feedback
            </Button>
          ),
        }}
      />
    </>
  )
}
